import moment from 'moment';
import { Project } from 'api/project';
import { getDateAfterSomeNumberOfWorkingDays } from 'components/organisms/timesheets/common/utils';
import { DefaultWorkingDayConfig } from 'components/organisms/user-capacity';
import { defaultFormat } from 'utils/date-field/formatter';
import {
  ProjectPhaseEvent,
  ProjectTimelineEvent,
  SchedulerEventType,
} from '../../event-types';
import { concat } from '../common';
import { resourceIdForProject } from '../id';
import { getSchedulerEventProps } from './common';
import { arrangeProjectPhases, phaseAsEvent } from './project-phase';

const projectAsEvent = ({
  startDate,
  dueDate,
  projectId,
}: Project): ProjectTimelineEvent => ({
  type: SchedulerEventType.ProjectTimeline,
  id: `${projectId}_timeline`,
  startDate: moment(startDate).startOf('day').toDate(),
  endDate: (dueDate != null ? moment(dueDate) : moment(startDate).add(1, 'day'))
    .endOf('day')
    .toDate(),
  resourceId: resourceIdForProject(projectId),
  ...getSchedulerEventProps(SchedulerEventType.ProjectTimeline),
});

const adjustStartDateAndDuration = (
  start: string,
  due: string,
  relativeDate: moment.Moment,
) => {
  const phaseStateDate = moment(start);
  const startDate = phaseStateDate.diff(relativeDate, 'days');
  const phaseEndDate = moment(due);
  const duration = phaseEndDate.diff(phaseStateDate, 'days') + 1;
  const adjustedStartDate = getDateAfterSomeNumberOfWorkingDays(
    relativeDate,
    startDate,
    DefaultWorkingDayConfig,
  );
  const adjustedEndDate = getDateAfterSomeNumberOfWorkingDays(
    adjustedStartDate,
    duration - 1,
    DefaultWorkingDayConfig,
  );

  return {
    adjustedStartDate: defaultFormat(adjustedStartDate),
    adjustedDueDate: defaultFormat(adjustedEndDate),
  };
};

export const convertProjectTimelineAndPhasesToEvents = (
  projects: Project[],
  isHoursPerWeek: boolean | undefined,
  relativeDate: moment.Moment,
): (ProjectTimelineEvent | ProjectPhaseEvent)[] =>
  projects
    .map((project) => {
      if (isHoursPerWeek) {
        // Since we hide weekends in template scheduler, this piece of code
        // ensures that the project start dates and durations are properly adjusted.
        const { adjustedStartDate, adjustedDueDate } =
          adjustStartDateAndDuration(
            project.startDate,
            project.dueDate,
            relativeDate,
          );

        project.startDate = adjustedStartDate;
        project.dueDate = adjustedDueDate;
      }

      const projectPhaseEvents = arrangeProjectPhases(
        project.projectPhases ?? [],
      ).map((phase) => {
        if (isHoursPerWeek) {
          // Since we hide weekends in template scheduler, this piece of code
          // ensures that the phase start dates and durations are properly adjusted.
          const { adjustedStartDate, adjustedDueDate } =
            adjustStartDateAndDuration(
              phase.startDate,
              phase.dueDate,
              relativeDate,
            );

          phase.startDate = adjustedStartDate;
          phase.dueDate = adjustedDueDate;
        }

        return phaseAsEvent(project.projectId, phase);
      });

      return concat(
        projectPhaseEvents,
        project.startDate != null ? [projectAsEvent(project)] : [],
      );
    })
    .reduce((result, events) => [...result, ...events], []);

export const convertProjectTimelineToEvents = (
  projects: Project[],
): ProjectTimelineEvent[] =>
  projects.filter(({ startDate }) => startDate != null).map(projectAsEvent);
